"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PRIVACY_POLICY = void 0;
var PRIVACY_POLICY = {
    title: "Privacy Policy",
    lastModified: "25 September 2026",
    sections: [
        {
            heading: "Introduction",
            blocks: [
                {
                    type: "paragraph",
                    text: "Cannaledger (PTY) Limited understands the importance of protecting your personal information and we value and respect it.",
                },
                {
                    type: "paragraph",
                    text: "This Privacy Statement describes how Cannaledger collects and uses your personal information, with whom we share it, and your choices and rights in relation to your personal information.",
                },
                {
                    type: "paragraph",
                    text: "It applies to personal information that we collect from you physically or in a borderless digital environment during your interactions with us, whether online, including through our websites (including mobile sites), social media sites and mobile applications (collectively “Internet Services”) that link to this Privacy Statement, in writing or orally, or personal information that we may collect offline or receive from third parties.",
                },
            ],
        },
        {
            heading: "Personal information we collect",
            blocks: [
                {
                    type: "paragraph",
                    text: "Cannaledger is subject to the Protection of Personal Information Act, No. 4 of 2013 (“POPIA”). “Personal information” is defined in POPIA as information relating to an identifiable, living, natural person and where it is applicable, an identifiable, existing juristic person, including but not limited to:",
                },
                {
                    type: "list",
                    items: [
                        {
                            text: "(a) information relating to the race, gender, sex, pregnancy, marital status, national, ethnic or social origin, colour, sexual orientation, age, physical or mental health, well-being, disability, religion, conscience, belief, culture, language and birth of the person;",
                        },
                        { text: "(b) information relating to the education or the medical, financial, criminal or employment history of the person;" },
                        {
                            text: "(c) any identifying number, symbol, e-mail address, physical address, telephone number, location information, online identifier or other particular assignment to the person;",
                        },
                        { text: "(d) the biometric information of the person;" },
                        { text: "(e) the personal opinions, views or preferences of the person;" },
                        {
                            text: "(f) correspondence sent by the person that is implicitly or explicitly of a private or confidential nature or further correspondence that would reveal the contents of the original correspondence;",
                        },
                        { text: "(g) the views or opinions of another individual about the person; and" },
                        {
                            text: "(h) the name of the person if it appears with other personal information relating to the person or if the disclosure of the name itself would reveal information about the person.",
                        },
                    ],
                },
                { type: "paragraph", text: "Depending on how you interact with us, personal information we collect may include but without limitation:" },
                {
                    type: "list",
                    items: [
                        { text: "your name" },
                        { text: "email address" },
                        { text: "postal address" },
                        { text: "telephone number (fixed or mobile)" },
                        { text: "log-in and account information for authentication purposes and account access" },
                        { text: "your gender and" },
                        { text: "social media account information." },
                    ],
                },
                {
                    type: "paragraph",
                    text: "We may also collect other information that does not personally identify you. Such other information includes browser and device information, website and application usage data, IP addresses, demographic information such as marketing preferences, geographic location, home language, and information collected through cookies and other technologies or information that has been anonymised or aggregated. If we link this information with your personal information, we will treat such linked information as personal information.",
                },
                {
                    type: "paragraph",
                    text: "You can choose not to provide personal information to us when requested. However, if this is necessary to provide you with our solutions, products and services, access to our Internet Services, or to perform administrative functions, we may be unable to provide you with our solutions or services. Cannaledger may also where applicable, be subject to the General Data Protection Regulations 2016/679 of the European Union [‘EU’] (“GDPR”), which is the primary legislation dealing with the protection and regulation of personal information. If you are a natural person based in the EU, who wish to or have procured products or services offered by Cannaledger and in the process you have provided us with personal information in order to process your enquiry and/or transaction or contract, we will supplement our obligations as set out herein with additional aspects of GDPR, where appropriate and applicable, some of which are referred to under section 10.",
                },
                {
                    type: "paragraph",
                    text: "Cannaledger may also where applicable, be subject to the Data Protection Act 2018 (“DPA 2018”), and the UK General Data Protection Regulation (“UK GDPR”) of the United Kingdom [‘UK’], which is the primary legislation dealing with the protection and regulation of personal information for citizens of the UK. If you are a natural person based in the UK, who wish to or have procured products or services offered by Cannaledger and in the process you have provided us with personal information in order to process your enquiry and/or transaction or contract, we will supplement our obligations as set out herein with additional aspects of the UK GDPR, where appropriate and applicable, some of which are referred to under section 10.",
                },
            ],
        },
        {
            heading: "How we collect your personal information",
            blocks: [
                { type: "paragraph", text: "We may collect your personal information from you in a variety of ways when you interact with us, including:" },
                {
                    type: "list",
                    items: [
                        {
                            text: "When you access our Internet Services, or interact with us in any other way, or use our solutions, products and services.",
                            subItems: [
                                "We collect personal information when you place orders for our solutions, products and services, you create an account with us, we process your orders and payment transactions, perform administrative and business functions, market our solutions, products and services to you and when you register for our events, workshops and seminars or subscribe to our mailing lists and newsletters.",
                            ],
                        },
                        {
                            text: "When you communicate with us.",
                            subItems: [
                                "We collect personal information when we respond to your inquiries and requests, obtain feedback from you about our solutions, products and services or you apply for employment with us.",
                            ],
                        },
                        {
                            text: "From third party sources.",
                            subItems: [
                                "We collect personal information from third parties, including public databases, social media sites, business partners with whom we offer co-branded services or engage in joint marketing activities and third parties that provide list enhancement or similar services.",
                            ],
                        },
                    ],
                },
                {
                    type: "paragraph",
                    text: "When you use our Internet Services, we may, and third parties we engage may automatically collect data, including personal information through use of cookies and similar technologies. For more information, see the “Cookies and Similar Technologies” section below.",
                },
            ],
        },
        {
            heading: "Legal basis for processing your personal information",
            blocks: [
                {
                    type: "paragraph",
                    text: "When we process your personal information in connection with the purposes set out in this Privacy Statement, we may rely on one or more of the following legal bases, depending on the purpose for which the processing activity is undertaken and the nature of our relationship with you:",
                },
                {
                    type: "list",
                    items: [
                        {
                            text: "Our legitimate interests (or those of a third party with whom we share your personal information) for the purpose of managing, operating or promoting our business, including direct marketing of personal information for business and administrative purposes, except where such interests are overridden by your interests or fundamental rights or freedoms which require protection of personal information.",
                        },
                        { text: "Where this is necessary to comply with a legal obligation on us, whether contractually or otherwise." },
                        { text: "To protect the vital interests of any individual." },
                        { text: "Where you have consented for such processing to take place." },
                    ],
                },
            ],
        },
        {
            heading: "Use of your personal information",
            blocks: [
                { type: "paragraph", text: "We may use your personal information to:" },
                {
                    type: "list",
                    items: [
                        {
                            text: "Enable you to effectively use and to improve our solutions, products and services, for example to:",
                            subItems: [
                                "Perform administrative and business functions and internal reporting.",
                                "Send administrative information to you.",
                                "Obtain feedback from you about our services, products and solutions including through client satisfaction surveys, in which event, we will only use your personal information for the sole purpose of sending you a survey.",
                                "Respond to your inquiries and fulfill requests by you.",
                                "Assess the performance of our Internet Services and to improve their operation.",
                                "Inform you about and provide you with our products, services and solutions.",
                                "Update our records and keep your contact details up to date.",
                            ],
                        },
                    ],
                },
                {
                    type: "paragraph",
                    text: "We engage in these activities to manage our contractual relationship with you, to comply with our legal obligations, or for our legitimate business interests:",
                },
                {
                    type: "list",
                    items: [
                        {
                            text: "Provide you with marketing materials and to personalise your experience with us, for example to:",
                            subItems: ["Send marketing communications to you.", "Enable you to subscribe to our newsletters and mailing lists."],
                        },
                    ],
                },
                { type: "paragraph", text: "We engage in these activities with your consent or for our legitimate business interests:" },
                {
                    type: "list",
                    items: [
                        {
                            text: "Achieve our business purposes and analyse information. For example, to:",
                            subItems: [
                                "Establish, manage, and maintain our business relationship with you.",
                                "Compile usage statistics for our Internet Services.",
                                "Process and respond to privacy requests, questions, concerns and complaints.",
                                "Fulfil legal and contractual obligations.",
                            ],
                        },
                    ],
                },
                {
                    type: "paragraph",
                    text: "We engage in these activities to manage our contractual relationship with you, to comply with a legal obligation and for our legitimate business interests.",
                },
            ],
        },
        {
            heading: "Your rights as a data subject",
            blocks: [
                {
                    type: "paragraph",
                    text: "As a data subject you have the right to have your personal information processed in accordance with the conditions for the lawful processing of personal information as referred to in Chapter 3 of POPIA, including the right—",
                },
                {
                    type: "list",
                    items: [
                        {
                            text: "to be notified that—",
                            subItems: [
                                "personal information about you is being collected as provided for in terms of section 18; or",
                                "your personal information has been accessed or acquired by an unauthorised person as provided for in terms of section 22;",
                            ],
                        },
                        {
                            text: "to establish whether a responsible party holds personal information of you and to request access to your personal information as provided for in terms of section 23;",
                        },
                        {
                            text: "to request, where necessary, the correction, destruction or deletion of your personal information as provided for in terms of section 24;",
                        },
                        {
                            text: "to object, on reasonable grounds relating to your particular situation to the processing of your personal information as provided for in terms of section 11 (3) (a);",
                        },
                        {
                            text: "to object to the processing of your personal information—",
                            subItems: ["at any time for purposes of direct marketing in terms of section 11 (3) (b); or", "in terms of section 69 (3) (c);"],
                        },
                        {
                            text: "not to have your personal information processed for purposes of direct marketing by means of unsolicited electronic communications except as referred to in section 69 (1);",
                        },
                        {
                            text: "not to be subject, under certain circumstances, to a decision which is based solely on the basis of the automated processing of your personal information intended to provide a profile of you as provided for in terms of section 71;",
                        },
                        {
                            text: "to submit a complaint to the Regulator regarding the alleged interference with the protection of your personal information as a data subject or to submit a complaint to the Regulator in respect of a determination of an adjudicator as provided for in terms of section 74; and",
                        },
                        {
                            text: "to institute civil proceedings regarding the alleged interference with the protection of your personal information as provided for in section 99.",
                        },
                    ],
                },
                { type: "paragraph", text: "All references to sections above are to sections in POPIA." },
            ],
        },
        {
            heading: "Sharing your personal information",
            blocks: [
                { type: "paragraph", text: "We may share your personal information for the purposes set out in this Privacy Statement (as applicable):" },
                {
                    type: "list",
                    items: [
                        {
                            text: "With our subsidiaries for the purposes set out in this Privacy Statement. Cannaledger is the party responsible for the management of jointly used personal information.",
                        },
                        { text: "With business partners with whom we offer co-branded services or engage in joint marketing activities." },
                        {
                            text: "With service providers to provide operational services or facilitate transactions on our behalf, including but not limited to processing of orders, assisting with sales-related activities or post-sales support, client support, email delivery, data analytics and auditing.",
                        },
                        { text: "Where you consent to the sharing of your personal information." },
                        {
                            text: "In connection with any joint venture, merger, sale of company assets, consolidation or restructuring, financing, or acquisition of all or a portion of our business by or to another company.",
                        },
                        { text: "Where applicable, for credit reference, fraud prevention or business scoring agencies, or other credit scoring agencies." },
                        { text: "For debt collection agencies or other debt recovery organisations." },
                        {
                            text: "For other legal reasons:",
                            subItems: [
                                "We may share your personal information in response to a request for information by a competent authority in accordance with, or required by any applicable law, regulation or legal process;",
                                "Where necessary to comply with judicial proceedings, court orders or government orders; or",
                                "To protect the rights, property or safety of Cannaledger, its business partners, you, or others, or as otherwise required by applicable law.",
                            ],
                        },
                    ],
                },
                {
                    type: "paragraph",
                    text: "Any third parties with whom we share personal information are contractually required to implement appropriate data protection and security measures to protect personal information and are not permitted to use personal information for any purpose other than the purpose for which they are provided with or given access to personal information.",
                },
            ],
        },
        {
            heading: "Security of your personal information",
            blocks: [
                {
                    type: "paragraph",
                    text: "Cannaledger is committed to protecting your personal information from accidental or unlawful destruction, damage, loss, alteration, unauthorised access or disclosure by using reasonable, appropriate, physical, administrative and technical safeguards and contractually requiring that third parties to whom we disclose your personal information do the same.",
                },
            ],
        },
        {
            heading: "Cross border transfers",
            blocks: [
                {
                    type: "paragraph",
                    text: "Cannaledger may be required to fulfil its contractual obligations with you outside of South Africa and may transfer your personal information to countries where we do business or to international organisations in connection with the purposes identified above and in accordance with this Privacy Statement.",
                },
            ],
        },
        {
            heading: "Your rights under data protection laws in the European Union (EU) and United Kingdom (UK)",
            blocks: [
                {
                    type: "paragraph",
                    text: "Cannaledger adheres to applicable data protection laws in the EU and UK, which provide you with certain rights relating to your personal information (if you are an EU or UK citizen), subject to any legal exceptions. Your rights include:",
                },
                {
                    type: "list",
                    items: [
                        { text: "The right to access personal information that we hold about you." },
                        {
                            text: "The right to rectify inaccurate personal information we hold about you without undue delay, and considering the purposes of the processing, to have incomplete personal information about you completed.",
                        },
                        { text: "The right to ask us to erase your personal information (the right to be forgotten) without undue delay in certain circumstances." },
                        { text: "The right to restrict the processing of your personal information in certain circumstances." },
                        {
                            text: "The right to receive your personal information from us in a structured, commonly used and machine-readable format and to transmit your personal information to a third party without obstruction (right to data portability) in certain circumstances.",
                        },
                        {
                            text: "Where we process personal information based on your consent, you have the right to withdraw your consent at any time for future processing.",
                        },
                        {
                            text: "Where we process your personal information based upon our legitimate interests or those of a third party, you have the right to object to the processing of your personal information at any time (including to any profiling).",
                        },
                        {
                            text: "Where we process your personal information for direct marketing purposes, you have the right to object to processing of your personal information at any time, including profiling to the extent that it is related to such direct marketing.",
                        },
                        {
                            text: "The right not to be subject to a decision based solely on automated processing, including profiling, which produces legal effects concerning you or similarly significantly affects you.",
                        },
                    ],
                },
                {
                    type: "paragraph",
                    text: "You may lodge a complaint with the European Commission’s online complaint procedure (https://ec.europa.eu) or the United Kingdom’s Information Commissioner’s Office (https://ico.org.uk/) if you believe that your data protection rights relating to your personal information have been breached by Cannaledger or that your personal information has been compromised in some way. You may contact us with requests, complaints or questions regarding these rights as set forth in the “How to contact us” section, below.",
                },
                {
                    type: "paragraph",
                    text: "Similarly, individuals in countries outside of the EU may exercise their rights under any applicable data protection laws by contacting us in accordance with the “How to contact us” section, below.",
                },
            ],
        },
        {
            heading: "Cookies and Similar Technologies",
            blocks: [
                {
                    type: "paragraph",
                    text: "When you access our Internet Services, we use cookies (small text files containing a unique ID number which are placed on your PC or device) and similar technologies including scripts, embedded web links and web beacons. We use cookies to assist us with activities such as:",
                },
                {
                    type: "list",
                    items: [
                        { text: "Enabling you to sign into our Internet Services;" },
                        { text: "Authenticating you;" },
                        { text: "Keeping track of information, you have provided to us;" },
                        { text: "Improving your browsing experience;" },
                        { text: "Customising our interactions with you;" },
                        { text: "Storing and managing your preferences and settings;" },
                        { text: "Compiling statistical data;" },
                        { text: "Analysing the performance and usability of our Internet Services;" },
                        { text: "Measuring traffic patterns for our Internet Services; and" },
                        { text: "Determining which areas of our Internet Services have been visited." },
                    ],
                },
                {
                    type: "paragraph",
                    text: "These technologies collect information that your browser sends to our Internet Services including your browser type, information about your IP address (a unique identifier assigned to your computer or device which allows your PC or device to communicate over the Internet), together with the date, time and duration of your visit, the pages you view and the links you click.",
                },
                {
                    type: "paragraph",
                    text: "Our Internet Services may also contain web beacons or similar technologies from third party analytics providers, through which they collect information about your activities across our Internet Services to help us compile aggregated statistics.",
                },
            ],
        },
        {
            heading: "Links to third party websites and applications",
            blocks: [
                {
                    type: "paragraph",
                    text: "Our Internet Services may contain links to third party websites and applications. We are not responsible for and make no representations or warranties in relation to the privacy practices or content of any third-party websites and applications. Your use of such sites and applications is subject to the applicable third party privacy statement and is at your own risk.",
                },
            ],
        },
        {
            heading: "Direct marketing",
            blocks: [
                {
                    type: "paragraph",
                    text: "We may send you unsolicited direct marketing e-communications about our solutions, products and services. You can choose whether you wish to receive marketing e-communications from Cannaledger by email, SMS, post and phone. You may opt out of receiving marketing materials from us at any time and manage your communication preferences by:",
                },
                {
                    type: "list",
                    items: [
                        { text: "Following the unsubscribe instructions included in each marketing email or SMS text message from us;" },
                        { text: "The Cannaledger website; or" },
                        {
                            text: "Sending an email to sales@cannaledger.com",
                            subItems: ["Including your details and a description of the marketing material you no longer wish to receive from us."],
                        },
                    ],
                },
                { type: "paragraph", text: "We will comply with your request as soon as is reasonably practicable." },
                {
                    type: "paragraph",
                    text: "If you opt out of receiving direct marketing related communications from us, we may still send you administrative messages as part of your ongoing use of our products, solutions and services, which you will be unable to opt out of.",
                },
                {
                    type: "paragraph",
                    text: "We do not provide your personal information to unaffiliated third parties for direct marketing purposes or sell, rent, distribute or otherwise make personal information commercially available to any third party.",
                },
            ],
        },
        {
            heading: "Retaining your personal information",
            blocks: [
                {
                    type: "paragraph",
                    text: "We will retain your personal information for as long as is necessary to fulfil the purpose for which it was collected unless a longer retention period is required to comply with legal obligations, resolve disputes, protect our assets, or enforce agreements. The criteria we use to determine retention periods include whether:",
                },
                {
                    type: "list",
                    items: [
                        {
                            text: "We are under a legal, contractual or other obligation to retain personal information, or as part of an investigation or for litigation purposes;",
                        },
                        { text: "Personal information is needed to maintain accurate business and financial records;" },
                        { text: "There are automated means to enable you to access and delete your personal information at any time;" },
                        {
                            text: "You have consented to us retaining your personal information for a longer retention period, in which case, we will retain personal information in line with your consent.",
                        },
                    ],
                },
            ],
        },
        {
            heading: "Updates to this Privacy Statement",
            blocks: [
                {
                    type: "paragraph",
                    text: "We may update this Privacy Statement at any time. If we do, we will update the “last modified” section at the bottom of this Privacy Statement.",
                },
                { type: "paragraph", text: "We encourage you to regularly review this Privacy Statement to stay informed about our privacy practices." },
            ],
        },
        {
            heading: "How to contact us",
            blocks: [
                {
                    type: "paragraph",
                    text: "If you have any requests, complaints or questions about this Privacy Statement or how we handle your personal information, please contact us by email at sales@cannaledger.com or by phone on +27 76 855 6011.",
                },
            ],
        },
    ],
};
exports.PRIVACY_POLICY = PRIVACY_POLICY;
